<template>
  <div
    class="page-nav"
    :class="{ collapsed: productsViewStore.shouldBeCollapsed }"
    :style="[{ maxWidth: `calc(100vw - ${getScrollbarWidth()}px)` }, store.topMenuStyle]"
  >
    <div ref="menu" class="menu" :style="[{ maxHeight: `${mobileMenuOpen ? menu?.scrollHeight : 0}px` }, store.topMenuStyle]">
      <div class="menu-items">
        <div class="link-group">
          <button class="btn btn-outline-primary popup-trigger" @click="emit('filters', { value: true })">
            <i class="fa fa-sliders mr-1" />
            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
              {{ appTranslate('Filter') }}
            </span>
          </button>
        </div>

        <div class="link-group">
          <button
            class="view-toggle popup-trigger"
            :class="[productsViewStore.view === 'list' ? 'active-view' : 'inactive-view']"
            @click="emit('switch-view', { value: 'list' })"
          >
            <!-- <i :class="`fa fa-id-card-o`" /> -->
            <i :class="`fa fa-list`" />
            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
              {{ appTranslate('List view') }}
            </span>
          </button>
          <button
            class="view-toggle popup-trigger"
            :class="[productsViewStore.view === 'table' ? 'active-view' : 'inactive-view']"
            @click="emit('switch-view', { value: 'table' })"
          >
            <i :class="`fa fa-table`" />
            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
              {{ appTranslate('Table view') }}
            </span>
          </button>
          <button
            class="view-toggle popup-trigger"
            :class="[productsViewStore.view === 'grid' ? 'active-view' : 'inactive-view']"
            @click="emit('switch-view', { value: 'grid' })"
          >
            <i :class="`fa fa-th-large`" />
            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
              {{ appTranslate('Grid view') }}
            </span>
          </button>
        </div>

        <div v-if="store.userPrivileges.has('export')" class="link-group">
          <button
            :disabled="actionsDisabled"
            class="btn btn-outline-secondary popup-trigger"
            :title="actionsDisabled ? appTranslate('Cannot export data when in edit mode') : undefined"
            @click="emit('export', { value: true })"
          >
            <i class="fa fa-file-o" />

            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
              {{ appTranslate('Export selection') }}
            </span>
          </button>

          <button
            v-if="store.userPrivileges?.has('manageSyncToWeb')"
            class="btn btn-outline-success popup-trigger"
            :disabled="actionsDisabled"
            :title="actionsDisabled ? appTranslate('') : undefined"
            @click="emit('sync', { value: true })"
          >
            <i class="fa fa-long-arrow-up" />
            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">Synkronisera</span>
          </button>
        </div>

        <div v-if="store.userPrivileges.has('manageProducts')" class="link-group">
          <router-link class="btn btn-outline-success popup-trigger" :to="{ name: 'new-product', state: { modal: true } }">
            <i class="fa fa-plus" />

            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
              {{ appTranslate('Create new product') }}
            </span>
          </router-link>

          <!-- <router-link class="btn btn-outline-primary" :to="{ name: 'product-comparison' }">
            <i class="fa fa-balance-scale" />

            <span class="description" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
              {{ appTranslate('Compare products') }}
            </span>
          </router-link> -->
        </div>
      </div>

      <!-- Links -->
      <template v-if="!productsViewStore.shouldBeCollapsed">
        <div v-for="(linkGroup, i) of store.links.productsArchiveMenu" :key="i" class="menu-items links">
          <!-- class="menu-items white-space-nowrap"
        :class="{ hidden: productsViewStore.shouldBeCollapsed }" -->
          <div>
            {{ appTranslate(linkGroup.header) }}
          </div>

          <router-link v-for="(link, j) of linkGroup.links" :key="j" :to="link.url" class="ml-2">
            {{ link.label }}
          </router-link>
        </div>
      </template>

      <button
        v-if="!productsViewStore.isMobileWidth"
        class="expand-collapse"
        :title="appTranslate(productsViewStore.shouldBeCollapsed ? 'Expand menu' : 'Collapse menu')"
        @click="productsViewStore.toggleMenuCollapsed()"
      >
        <i class="fa" :class="[productsViewStore.shouldBeCollapsed ? 'fa-chevron-right' : 'fa-chevron-left']" />
      </button>

      <div class="environment-msg" :class="[store.themeEnvironment]">
        <div v-if="store.isDevEnvironment" class="dev-buttons">
          <button class="btn btn-warning dev" @click="console.log(clone(productsStore.currentProducts))">Products</button>
          <button class="btn btn-warning dev" @click="console.log(clone(bulkStore.modified))">Modified</button>
          <button class="btn btn-warning dev" @click="console.log(clone(bulkStore.currentState))">Inputs</button>
          <button class="btn btn-warning dev" @click="console.log(clone(bulkStore.debugHistory))">History</button>
          <button class="btn btn-light dev" @click="productsStore.currentProducts[299] = undefined">Insert empty @ 299</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from '@/composables/useTranslation'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { useProductsStore } from '@/store/productsArchiveStore'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { useProductsViewStore } from '@/store/productsViewStore'
import { clone, getScrollbarWidth } from '@/utils/tools'
import { computed, onMounted, ref } from 'vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{ hasResult: boolean }>()
const emit = defineEmits(['filters', 'export', 'sync', 'switch-view'])

const { translate, appTranslate } = useTranslation()
const store = useDefaultStore()
const productsViewStore = useProductsViewStore()

// dev only
const productsStore = useProductsStore()
const bulkStore = useProductsBulkStore()

// ---------------
// -- Reactives --
// ---------------
const mobileMenuOpen = ref(false)
const menu = ref<HTMLElement>()

const actionsDisabled = computed(() => !props.hasResult || bulkStore.editMode)

// ---------------------
// -- Lifecycle hooks --
// ---------------------
onMounted(() => {
  $('#collapsible-navbar').on('show.bs.collapse', () => {
    mobileMenuOpen.value = true
  })

  $('#collapsible-navbar').on('hide.bs.collapse', () => {
    mobileMenuOpen.value = false
  })

  DataService.loadLinks('productsArchiveMenu')
})
</script>

<style lang="scss" scoped>
@use '@/styles/popup.scss';

.page-nav {
  position: sticky;
  left: 0;
  top: 0;
  z-index: 10;
  background-color: white;
  box-shadow: 7px 0 10px -10px #0002;
  margin-bottom: 1em;

  &:not(.collapsed) {
    white-space: nowrap;

    @media (max-width: 768px) {
      min-width: calc(100vw - 17px); // imperfect. getScrollbarWidth() is not reactive, and not relevant for mobile
    }

    button,
    a {
      overflow: hidden;
    }

    .link-group {
      .btn {
        text-align: left;
        padding-left: 1.5em;

        i {
          text-align: center;
          width: 1em;
        }
      }
    }
  }

  .menu {
    position: sticky;
    user-select: none;

    .menu-items {
      padding: 1em;
      width: 100%;

      &.links {
        overflow-x: hidden;
      }
    }

    .link-group {
      width: 100%;
      display: flex;
      flex-direction: column;

      button,
      a {
        margin: 0 2px 2px 2px;
        padding-left: 0.65em;
        padding-right: 0.65em;
      }

      &:not(:last-child) {
        margin-bottom: 1em;
        padding-bottom: 1em;
        border-bottom: 1px solid #eee;
      }

      .btn {
        width: 100%;
      }
    }

    .description {
      margin-left: 0.5em;
    }
  }

  .view-toggle {
    width: 100%;
    display: block;
    border-radius: 3px;
    cursor: pointer;
    border: none;
    outline: none;
    background-color: transparent;
    text-align: left !important;
  }
  .active-view {
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    // color: var(--primary);
  }

  .inactive-view {
    color: #bbb;
  }

  .mobile-menu-toggler {
    display: none;
  }

  @media (max-height: 500px) {
    overflow-y: auto;

    .menu {
      top: 0 !important;
    }
  }

  @media (min-width: 769px) {
    width: 100% !important;
  }

  @media (max-width: 768px) {
    overflow: hidden;
    height: auto;
    box-shadow: none;
    box-shadow: 0 7px 10px -10px #0002;

    .mobile-menu-toggler {
      display: inline-block;
      background-color: transparent;
      outline: none;
      border: none;
    }

    .menu {
      display: flex;
      flex-wrap: wrap;
      transition: max-height 0.3s ease;
      box-sizing: border-box;
      overflow: hidden;

      .menu-items {
        padding: 0.5em;
      }

      &.collapsed {
        max-height: 0;
      }

      .link-group {
        .btn {
          width: unset;
        }
      }
    }
  }
}

.environment-msg {
  width: 100%;
  text-align: center;
  color: white;

  &::before {
    display: block;
    padding: 1em;
  }

  background-color: var(--theme-bg);

  &.development {
    &::before {
      content: 'DEV';
    }
  }

  &.stage {
    &::before {
      content: 'STAGE';
    }
  }

  .dev-buttons {
    display: grid;
    row-gap: 5px;
    grid-template-rows: 1fr 1fr 1fr;
    padding-bottom: 3em;

    button {
      font-size: 0.8em;
      padding: 3px 5px;
      margin: 0 4px;
    }
  }
}

.collapsed {
  i {
    // center icon
    width: 100%;
    text-align: center;
  }
}

.popup {
  top: 50%;
  // left: calc(100% + 0.5em);
  left: 100%;
  transform: translate(0, -50%);
}

.expand-collapse {
  width: 100%;
  padding: 1rem 1.5rem;
  border: 1px solid #eee;
  border-left: none;
  border-right: none;
  background-color: transparent;
  outline: none;
  text-align: end;
}

button:disabled {
  opacity: 1;
}
</style>
